import { CircularProgress, TextField } from "@mui/material";

import PopupDialog from "../PopupDialog";
import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import {
  CalloutText,
  DialogSheet,
  ErrorNote,
  Footnote,
  InfoCallout,
  SectionLabel,
  SegmentedChoice,
  figmaButtonSx,
} from "./settlementStyles";

// The challenge form, from Figma node 1720:7104. Lifted out of SettlementPanel, which carried it
// inline; the panel keeps the state and the contract call and this only renders them.
//
// V10.1: the call is EcoChallenge.raiseChallenge(tokenId, kind, evidence) — a direct wallet
// transaction, no longer EcoBank.challengeDeed through the relay — so the form gained the KIND:
// FIELD (the challenger has been to the land) or EVIDENCE (a desk review of the deed record).
// A deed-stage challenge is decided by a single drawn checker. It HOLDS the settlement only when
// it takes the open deed window (`holdsSettlement`, read from windowOf by the panel); raised after
// the window closed it is recorded and adjudicated but the sale proceeds, and the copy says which.

export type DeedChallengeKind = "field" | "evidence";

const KIND_OPTIONS: { value: DeedChallengeKind; label: string }[] = [
  { value: "field", label: "Field" },
  { value: "evidence", label: "Evidence" },
];

export interface ChallengeDeedDialogProps {
  open: boolean;
  onClose: () => void;
  // Whether the deed review window is still open, so this challenge would hold the settlement.
  holdsSettlement?: boolean;
  kind: DeedChallengeKind | null;
  onKindChange: (kind: DeedChallengeKind) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  onSubmit: () => void;
  busy?: boolean;
  error?: string | null;
}

// The Figma field is a plain outlined box with placeholder copy inside — no floating label.
export const reasonFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "transparent",
    padding: "18px 17px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "14px",
    lineHeight: "18px",
    // .MuiOutlinedInput-notchedOutline, not a bare `fieldset` selector: MUI's own rule is
    // class-on-class, so an element selector loses to it and the border stays default grey.
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#121212", borderWidth: "1px" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#121212" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#121212", borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input::placeholder": { color: "#565656", opacity: 1 },
} as const;

const ChallengeDeedDialog = ({
  open,
  onClose,
  holdsSettlement = true,
  kind,
  onKindChange,
  reason,
  onReasonChange,
  onSubmit,
  busy,
  error,
}: ChallengeDeedDialogProps) => (
  <PopupDialog
    open={open}
    onClose={() => {
      if (!busy) {
        onClose();
      }
    }}
    maxWidth={420}
    surface="sheet"
  >
    <DialogSheet>
      <InfoCallout surface="white">
        <CalloutText>
          {holdsSettlement
            ? "The evidence below is recorded on-chain and holds the settlement until a drawn checker decides. "
            : "The deed review window has closed, so this challenge is recorded and decided by a drawn checker but " +
              "does not hold the settlement — the sale proceeds. "}
          If the challenge is dismissed, you are flagged. Sent as a wallet transaction — you pay the gas.
        </CalloutText>
      </InfoCallout>

      <SectionLabel>Kind of challenge</SectionLabel>
      <SegmentedChoice options={KIND_OPTIONS} value={kind} onChange={onKindChange} disabled={busy} />

      <TextField
        fullWidth
        multiline
        minRows={3}
        placeholder={
          kind === "evidence" ? "What in the deed record does not hold up?" : "What did you find on the land?"
        }
        value={reason}
        onChange={(e) => onReasonChange(e.target.value)}
        disabled={busy}
        sx={reasonFieldSx}
      />

      {error && <ErrorNote>{error}</ErrorNote>}

      {busy ? (
        <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
          <CircularProgress size={20} sx={{ color: "white" }} />
        </PopupProcessingButton>
      ) : (
        <PopupActionButton
          onClick={onSubmit}
          disabled={!reason.trim() || !kind}
          variant="contained"
          fullWidth
          sx={figmaButtonSx}
        >
          Challenge deed
        </PopupActionButton>
      )}

      <PopupGhostButton onClick={onClose} disabled={busy} fullWidth>
        Cancel
      </PopupGhostButton>

      <Footnote>
        Only an independent verifier may raise this, and each wallet may challenge a token once. The evidence becomes
        part of the on-chain record.
      </Footnote>
    </DialogSheet>
  </PopupDialog>
);

export default ChallengeDeedDialog;
